import React, { useCallback } from 'react'
import OptionalVariableCheckbox from './OptionalVariableCheckbox'
import FormBoxName from './FormBoxName'
import { useStore } from '../../../store/store';
import { isVariableCustom, mutateInjectVariablesInDocument } from '../../../utils/variables';
import Tooltip from '../../common/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useUpdateCustomTextVariable, useUpdateVariableGroup } from '../../../hooks';
import FormBoxRemark from './FormBoxRemark';
import FormPredefinedValues from './FormPredefinedValues';
import { convertToRaw } from 'draft-js';
import VariableSelectionGroup from '../VariableSelectionGroup';
import { constructVariableIds, mutateHandleLinkedVariable } from '../../../utils/variableGroups';

const selector = (id) =>
  ({ selectedBoxId, configuration, updateVariable, variables }) => {
    return {
      isSelected: selectedBoxId === id,
      updateVariable,
      configuration,
      variables,
    };
  };

const FormCustomTextInfos = ({ box, form, validateFormAndRetrieveValues }) => {
  const { updateVariable, variables } = useStore(selector(box.id));

  const {
    mutate: updateCustomTextVariable,
  } = useUpdateCustomTextVariable();

  const handleOnBlur = useCallback(async () => {
    if (box.id === 'create-variable') return
    // Ne mettre à jour en base que les variables vraiment custom
    if (!isVariableCustom(box.id)) return
    try {
      const values = await validateFormAndRetrieveValues();
      const id = box.id.substring(box.id.lastIndexOf('.') + 1)
      const remarkRaw = JSON.stringify(convertToRaw(values.remark.getCurrentContent()))

      updateCustomTextVariable({ id, name: values.name, remarks: remarkRaw }, {
        onSuccess: ({ id }) => {
          updateVariable(box.id, (variable, draft) => {
            draft.defaultVariablesApi.custom_text_variables[`${values.name}.${id}`] = draft.defaultVariablesApi.custom_text_variables[`${variable.name}.${id}`]
            delete draft.defaultVariablesApi.custom_text_variables[`${variable.name}.${id}`]
            variable.name = values.name;
            variable.remark = remarkRaw;
            mutateInjectVariablesInDocument({
              variables,
              variable,
              variableKey: box.id,
              draft
            })
          })
        },
        onError: (err) => {
          form.setFieldValue("name", box.name)
        }
      })
    } catch (errors) {
    }
  }, [box.id, box.name, form, updateCustomTextVariable, updateVariable, validateFormAndRetrieveValues, variables])


  const handleOnBlurRemark = async () => {
    if (box.id === 'create-variable') return
    // Ne pas appeler l'API pour les variables issues de l'API (non custom)
    if (!isVariableCustom(box.id)) return

    try {
      const values = await validateFormAndRetrieveValues();
      const id = box.id.substring(box.id.lastIndexOf('.') + 1)
      const remarkRaw = JSON.stringify(convertToRaw(values.remark.getCurrentContent()))

      updateCustomTextVariable(
        { id, name: values.name, remarks: remarkRaw },
        {
          onSuccess: () => {
            updateVariable(box.id, (variable) => {
              variable.remark = remarkRaw;
            })
          },
        }
      )
    } catch (errors) {
    }
  }
  const updateVariableGroup = useStore(({ updateVariableGroup }) => updateVariableGroup)
  const isTemplate = useStore(({ currentDocument }) => currentDocument.type === 'template')
  const variableGroups = useStore(({ variableGroups }) => variableGroups)

  const {
    mutate: updateVariableGroupMutation,
  } = useUpdateVariableGroup({
    onSuccess: (updatedVariableGroup, { variableKey, id }) => {
      updateVariableGroup(id,
        (variableGroup, draft) => {
          mutateHandleLinkedVariable({
            variableGroup,
            draft,
            variableIds: updatedVariableGroup.variable_ids,
            variableId: variableKey
          })
        })
    }
  });

  const handleOnBlurGroup = useCallback(async (oldGroupId) => {
    if (box.id === 'create-variable') return
    try {
      await validateFormAndRetrieveValues();
      const newGroupId = form.getFieldValue("linked-group")
      const variableKey = box.id;

      if (oldGroupId && oldGroupId !== newGroupId) {
        const variableGroup = variableGroups.find(
          (v) => v.id === oldGroupId
        )
        const variableIds = constructVariableIds({ variableGroup, variableId: variableKey })
        updateVariableGroupMutation({ id: variableGroup.id, variableIds, variableKey })
      }
      if (newGroupId && oldGroupId !== newGroupId) {
        const variableGroup = variableGroups.find(
          (v) => v.id === newGroupId
        )
        const variableIds = constructVariableIds({ variableGroup, variableId: variableKey })
        updateVariableGroupMutation({ id: variableGroup.id, variableIds, variableKey })
      }
    } catch (errors) {
    }

  }, [box.id, form, updateVariableGroupMutation, validateFormAndRetrieveValues, variableGroups])

  // flex 1 + minHeight 0 : la carte parente est en overflow hidden, sans ça un
  // formulaire trop haut était coupé au lieu de défiler. Le bouton de
  // suppression, lui, reste hors de cette zone et donc toujours en bas.
  return (
    <div className='variable-editor__left' style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: 8 }}>
      <div style={{
        display: 'flex',
        height: 'fit-content',
        alignItems: 'center'
      }}>
        <div style={{ flex: 1 }}>
          <FormBoxName
            box={box}
            onBlur={handleOnBlur}
            disabled={!isVariableCustom(box.id)}
          />
        </div>
        {!isVariableCustom(box.id) && <Tooltip
          trigger="hover"
          placement='bottom'
          mouseLeaveDelay='0'
          title="Variable en provenance de l'API">
          <FontAwesomeIcon
            icon={icon({ name: 'circle-info', style: 'solid' })}
            style={{ marginLeft: 8, marginBottom: 24, color: '#0b2239' }}
          />
        </Tooltip>
        }
      </div>
      <VariableSelectionGroup box={box} onBlur={handleOnBlurGroup} form={form} />
      <FormBoxRemark
        box={box}
        onBlur={handleOnBlurRemark}
        form={form}
        // En mode template, désactiver la remarque pour les variables non custom
        disabled={isTemplate && !isVariableCustom(box.id)}
      />
      {/* Réglage du modèle, hérité par les propositions (variables du document). */}
      {box.id !== 'create-variable' && (
        <OptionalVariableCheckbox
          checked={variables[box.id]?.optional}
          disabled={box.from_linked_section}
          onChange={(checked) => updateVariable(box.id, (variable) => {
            if (variable) variable.optional = checked;
          })}
        />
      )}
      {/* Les valeurs prédéfinies sont persistées sur la variable en base : elles
          n'ont donc de sens que pour les variables custom déjà créées. */}
      {box.id !== 'create-variable' && isVariableCustom(box.id) &&
        <FormPredefinedValues box={box} />}
    </div>
  )
}

export default FormCustomTextInfos
